import { useState, type FormEvent } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db7hp973hiaolq192hgg";

const contactInfo = [
  {
    icon: "ri-mail-line",
    label: "爆料邮箱",
    value: "tips@911baoliao.demo",
    note: "24 小时接收线索",
  },
  {
    icon: "ri-phone-line",
    label: "值班电话",
    value: "400-000-9110",
    note: "工作日 09:00 - 21:00",
  },
  {
    icon: "ri-map-pin-line",
    label: "办公地址",
    value: "示例市示例区真相路 911 号",
    note: "来访请提前预约",
  },
  {
    icon: "ri-shield-keyhole-line",
    label: "匿名线索",
    value: "支持匿名提交",
    note: "请注意保护个人信息",
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">(
    "idle"
  );
  const [formError, setFormError] = useState("");

  useDocumentTitle(
    "联系我们 - 911爆料",
    "通过电话、邮箱或在线反馈表单联系 911爆料，提供爆料线索或反馈建议。"
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get("website_alt") ?? "").trim();
    if (guard) {
      setStatus("success");
      form.reset();
      return;
    }

    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const code = parsed?.code;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && code === "OK";

      if (!isOk || isSpam) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请检查填写内容后重试。"
        );
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="animate-fade-up">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-shell px-4 py-14 md:px-6 md:py-16">
          <span className="font-label text-xs tracking-[0.3em] text-primary-700">
            CONTACT
          </span>
          <h1 className="mt-3 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            联系我们
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            无论是提供爆料线索、反馈报道问题，还是寻求合作，都欢迎与我们取得联系。我们珍视每一条线索，并会妥善保护提供者信息。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-shell px-4 py-12 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground-950">
              联系方式
            </h2>
            <div className="mt-5 space-y-4">
              {contactInfo.map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-800">
                    <i className={item.icon} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="mt-1 text-sm font-medium text-foreground-900">
                      {item.value}
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">{item.note}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 flex items-start gap-3 rounded-lg border border-accent-500/40 bg-accent-500/10 p-5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
                <i className="ri-error-warning-line" />
              </span>
              <p className="text-xs leading-relaxed text-foreground-700">
                以上联系方式与提交内容均为演示，不涉及真实存储与处理。
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                在线反馈
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                填写以下信息，我们会通过您留下的邮箱与您联系。
              </p>

              <form
                id="contact-feedback-form"
                data-readdy-form
                action={FORM_ENDPOINT}
                method="post"
                onSubmit={handleSubmit}
                className="mt-6 space-y-5"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="mb-2 block text-sm font-medium text-foreground-800"
                    >
                      姓名 <span className="text-primary-700">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入您的称呼"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-email"
                      className="mb-2 block text-sm font-medium text-foreground-800"
                    >
                      邮箱 <span className="text-primary-700">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="contact-subject"
                    className="mb-2 block text-sm font-medium text-foreground-800"
                  >
                    主题
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    defaultValue="提供爆料线索"
                    className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 focus:border-primary-500 focus:outline-none"
                  >
                    <option value="提供爆料线索">提供爆料线索</option>
                    <option value="反馈报道问题">反馈报道问题</option>
                    <option value="内容合作">内容合作</option>
                    <option value="其他建议">其他建议</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="contact-content"
                    className="mb-2 block text-sm font-medium text-foreground-800"
                  >
                    内容 <span className="text-primary-700">*</span>
                  </label>
                  <textarea
                    id="contact-content"
                    name="content"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="请简要描述您想反馈的内容（不超过 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm leading-relaxed text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                  />
                  <p className="mt-1.5 text-xs text-foreground-500">最多 500 字</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="lead-capture-guard"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <div className="flex items-start gap-2.5 rounded-md border border-primary-500/60 bg-primary-100/60 px-4 py-3 text-sm text-primary-800">
                    <i className="ri-error-warning-line mt-0.5" />
                    <span>{formError}</span>
                  </div>
                )}

                {status === "success" && (
                  <div className="flex items-start gap-2.5 rounded-md border border-accent-500/50 bg-accent-500/10 px-4 py-3 text-sm text-foreground-800">
                    <i className="ri-checkbox-circle-line mt-0.5 text-accent-400" />
                    <span>提交成功！感谢您的反馈，我们会尽快与您联系。</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交反馈
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}